import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Wallet,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  AlertTriangle,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AgentHeader } from '../../components/layout/AgentHeader';
import { AgentSidebar } from '../../components/layout/AgentSidebar';
import { BottomNav } from '../../components/layout/BottomNav';
import { Card, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { useFloatRecharge } from '../../hooks/useFloatRecharge';
import { useFloatRechargeSocket, useSocketStatus } from '../../hooks/useSocket';
import type {
  RechargeMethod,
  RechargeStatus,
  FloatRechargeRequest,
} from '../../types/float-recharge.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const METHOD_LABELS: Record<RechargeMethod, string> = {
  INTERNAL_TRANSFER: 'Transferencia Interna',
  BANK_TRANSFER: 'Transferencia Bancaria',
  CASH_DEPOSIT: 'Depósito en Efectivo',
  MOBILE_MONEY: 'Mobile Money',
  CASH_PICKUP: 'Recogida en Efectivo',
};

const METHODS: RechargeMethod[] = [
  'INTERNAL_TRANSFER',
  'BANK_TRANSFER',
  'CASH_DEPOSIT',
  'MOBILE_MONEY',
  'CASH_PICKUP',
];

const QUICK_AMOUNTS = [50_000, 100_000, 200_000, 500_000];

type StatusFilter = 'ALL' | RechargeStatus;

const STATUS_CONFIG: Record<
  RechargeStatus,
  { label: string; color: string; bg: string; Icon: React.ElementType }
> = {
  PENDING: {
    label: 'Pendiente',
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    Icon: Clock,
  },
  APPROVED: {
    label: 'Aprobada',
    color: 'text-green-700',
    bg: 'bg-green-100',
    Icon: CheckCircle,
  },
  REJECTED: {
    label: 'Rechazada',
    color: 'text-red-700',
    bg: 'bg-red-100',
    Icon: XCircle,
  },
};

// ─── Request Modal ────────────────────────────────────────────────────────────

function RequestModal({
  open,
  onClose,
  onCreate,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { amount: number; method: RechargeMethod; notes?: string }) => void;
  isPending: boolean;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<RechargeMethod>('INTERNAL_TRANSFER');
  const [notes, setNotes] = useState('');
  const [amountError, setAmountError] = useState('');

  // Reset form every time the modal is closed
  useEffect(() => {
    if (!open) {
      setAmount('');
      setMethod('INTERNAL_TRANSFER');
      setNotes('');
      setAmountError('');
    }
  }, [open]);

  const numAmount = parseFloat(amount.replace(/,/g, '')) || 0;

  const validate = () => {
    if (!numAmount || numAmount < 1000) {
      setAmountError('Mínimo 1,000 XAF');
      return false;
    }
    setAmountError('');
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onCreate({ amount: numAmount, method, notes: notes.trim() || undefined });
  };

  const handleClose = () => {
    setAmount('');
    setMethod('INTERNAL_TRANSFER');
    setNotes('');
    setAmountError('');
    onClose();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Solicitar Recarga de Float">
      <div className="space-y-5">
        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monto (XAF) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min={1000}
            placeholder="0"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setAmountError('');
            }}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {amountError && (
            <p className="text-xs text-red-500 mt-1">{amountError}</p>
          )}
          {/* Quick amounts */}
          <div className="flex gap-2 mt-2 flex-wrap">
            {QUICK_AMOUNTS.map((v) => (
              <button
                key={v}
                onClick={() => setAmount(String(v))}
                className="px-3 py-1 text-xs rounded-full border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
              >
                {v.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Método de pago
          </label>
          <div className="relative">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as RechargeMethod)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABELS[m]}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notas para el administrador
          </label>
          <textarea
            rows={3}
            placeholder="Opcional: motivo o referencia..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Summary */}
        {numAmount >= 1000 && (
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
            <p className="text-sm text-gray-600">
              Solicitarás{' '}
              <span className="font-bold text-purple-700">
                {numAmount.toLocaleString()} XAF
              </span>{' '}
              vía{' '}
              <span className="font-medium">{METHOD_LABELS[method]}</span>.
              El administrador deberá aprobar antes de que el saldo sea acreditado.
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <RefreshCw size={16} className="animate-spin" /> Enviando...
              </span>
            ) : (
              'Enviar Solicitud'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Request Card ─────────────────────────────────────────────────────────────

function RechargeCard({ req }: { req: FloatRechargeRequest }) {
  const cfg = STATUS_CONFIG[req.status];
  const StatusIcon = cfg.Icon;

  const rejectionReason = (() => {
    if (req.status !== 'REJECTED' || !req.notes) return null;
    const match = req.notes.match(/Rejection reason:\s*(.*)/);
    return match ? match[1].trim() : null;
  })();

  return (
    <div className="p-4 border border-gray-200 rounded-xl bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg font-bold text-gray-900">
              {req.amount.toLocaleString()} XAF
            </span>
            <span
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}
            >
              <StatusIcon size={11} />
              {cfg.label}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {METHOD_LABELS[req.method]} ·{' '}
            {format(new Date(req.requestedAt), "d MMM yyyy, HH:mm", { locale: es })}
          </p>
          {req.reference && (
            <p className="text-xs text-gray-400 mt-0.5">Ref: {req.reference}</p>
          )}
          {rejectionReason && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{rejectionReason}</span>
            </div>
          )}
        </div>
        {req.status === 'APPROVED' && req.approver && (
          <div className="text-right shrink-0">
            <p className="text-xs text-gray-400">Aprobado por</p>
            <p className="text-xs font-medium text-gray-700">
              {req.approver.firstName} {req.approver.lastName}
            </p>
            {req.approvedAt && (
              <p className="text-xs text-gray-400">
                {format(new Date(req.approvedAt), "d MMM HH:mm", { locale: es })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const FILTER_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'Todas', value: 'ALL' },
  { label: 'Pendientes', value: 'PENDING' },
  { label: 'Aprobadas', value: 'APPROVED' },
  { label: 'Rechazadas', value: 'REJECTED' },
];

export default function FloatRechargePage() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const { requests, isLoading, isError, createMutation } = useFloatRecharge(
    filter === 'ALL' ? undefined : filter
  );

  // Real-time updates via WebSocket
  useFloatRechargeSocket();
  const { connected: wsConnected } = useSocketStatus();

  const handleCreate = async (payload: {
    amount: number;
    method: RechargeMethod;
    notes?: string;
  }) => {
    try {
      await createMutation.mutateAsync(payload);
      setShowModal(false);
    } catch {
      // handled in hook
    }
  };

  const pendingCount = requests.filter((r) => r.status === 'PENDING').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AgentHeader />

      <div className="flex">
        <AgentSidebar />

        <main className="flex-1 p-4 lg:p-6 pb-20 lg:pb-6">
          {/* Page header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Recarga de Float
                  </h2>
                  {wsConnected ? (
                    <span
                      title="Tiempo real activo"
                      className="flex items-center gap-1 text-xs text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"
                    >
                      <Wifi size={11} />
                      En vivo
                    </span>
                  ) : (
                    <span
                      title="Sin conexión en tiempo real"
                      className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full"
                    >
                      <WifiOff size={11} />
                      Offline
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Solicita liquidez para operar
                </p>
              </div>
            </div>
            <Button onClick={() => setShowModal(true)}>
              <Plus size={18} className="mr-2" />
              Nueva Solicitud
            </Button>
          </div>

          {/* Info banner */}
          <div className="mb-6 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <Wallet className="text-blue-500 shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-blue-800">
              Las solicitudes son revisadas por el equipo de UrekaPay. Una vez
              aprobada, el monto se acredita automáticamente a tu float.
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === tab.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                {tab.value === 'PENDING' && pendingCount > 0 && (
                  <span className="ml-1.5 bg-yellow-400 text-yellow-900 text-xs px-1.5 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-20 bg-gray-200 rounded-xl animate-pulse"
                />
              ))}
            </div>
          ) : isError ? (
            <Card variant="bordered">
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Error al cargar las solicitudes</p>
              </CardContent>
            </Card>
          ) : requests.length === 0 ? (
            <Card variant="bordered">
              <CardContent className="py-16 text-center">
                <Wallet size={40} className="mx-auto text-gray-300 mb-3" />
                <p className="font-medium text-gray-700">
                  {filter === 'ALL'
                    ? 'No tienes solicitudes aún'
                    : `Sin solicitudes ${STATUS_CONFIG[filter as RechargeStatus]?.label.toLowerCase() ?? ''}`}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Presiona "Nueva Solicitud" para comenzar
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((req) => (
                <RechargeCard key={req.id} req={req} />
              ))}
            </div>
          )}
        </main>
      </div>

      <BottomNav />

      <RequestModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onCreate={handleCreate}
        isPending={createMutation.isPending}
      />
    </div>
  );
}
